import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { CoPilotProvider } from './context/CoPilotContext';
import { ShellStatusProvider } from './context/ShellStatusContext';

// Pages
import { LoginPage } from './pages/LoginPage';

// Layout (North Star shell — rail + topbar + ⌘K + CoPilot)
import AppShell from './components/layout/AppShell';

// Home
import HubHomePage from './pages/Hub/HubHomePage';

// Core Analysis Modules
import AirCapLogicPage from './pages/AirCapLogicPage';
import RunCapPage from './pages/RunCapPage';
import OptimizerPage from './pages/OptimizerPage';
import AirportDashboardPage from './pages/AirportDashboardPage';

// FTS (Fast Time Simulation)
import SimulationHomePage from './pages/SimulationHomePage';
import SimulationCreatePage from './pages/SimulationCreatePage';
import SimulationDetailPage from './pages/SimulationDetailPage';
import SimulationAnimationPage from './pages/SimulationAnimationPage';
import AITrainingPage from './pages/AITrainingPage';

// CoPilot (RAG Data Assistant)
import CoPilotPage from './pages/CoPilotPage';

// Utility
import { SettingsPage } from './pages/SettingsPage';
import ReportPage from './pages/ReportPage';
import AdminPage from './pages/AdminPage';
import { UserProfilePage } from './pages/UserProfilePage';
import { HomePage } from './pages/HomePage';
import { NewProjectPage } from './pages/NewProjectPage';

// Project detail: layout + one route per former tab
import ProjectLayout from './pages/project/ProjectLayout';
import OverviewPage from './pages/project/OverviewPage';
import ApcasPage from './pages/project/ApcasPage';
import EquilibriumPage from './pages/project/EquilibriumPage';
import AdosaPage from './pages/project/AdosaPage';
import KpisPage from './pages/project/KpisPage';
import MapasPage from './pages/project/MapasPage';
import EconomicsTab from './pages/project/EconomicsTab';
// WeatherDashboardPage (AeroForecast) demoted from a top-level module — code kept
// under pages/WeatherDashboardPage.tsx but no longer routed/linked. /aeroforecast
// now redirects to home (see LEGACY REDIRECTS below).
import StaffRosterPage from './pages/StaffRosterPage';
import EconomicsPage from './pages/EconomicsPage';

// Loading screen component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)', color: 'var(--text-primary)' }}>
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-[#F26430] border-t-transparent rounded-full animate-spin"></div>
      <p className="text-sm font-medium text-[var(--text-secondary)] tracking-wider animate-pulse">LOADING DACAMS...</p>
    </div>
  </div>
);

// Route protection component
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingScreen />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <CoPilotProvider>
            <ShellStatusProvider>
            <Router>
              <Routes>
                {/* --- PUBLIC ROUTE --- */}
                <Route path="/login" element={<LoginPage />} />

                {/* --- HOME (Hub — no sidebar) --- */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <HubHomePage />
                  </ProtectedRoute>
                } />

                {/* --- ALL OTHER ROUTES WRAPPED IN APPLAYOUT (persistent sidebar) --- */}

                {/* --- DACAMS ANALYSIS (Project Listing) --- */}
                <Route path="/analysis" element={
                  <ProtectedRoute>
                    <AppShell><HomePage /></AppShell>
                  </ProtectedRoute>
                } />

                {/* --- DACAMS ANALYSIS (Project-based) --- */}
                <Route path="/projects/new" element={
                  <ProtectedRoute>
                    <AppShell><NewProjectPage /></AppShell>
                  </ProtectedRoute>
                } />
                {/* Each former tab of the project detail monolith is now its own
                    route under the shared ProjectLayout, so the shell rail can
                    point at a module instead of at one catch-all page. */}
                <Route path="/projects/:id" element={
                  <ProtectedRoute>
                    <AppShell><ProjectLayout /></AppShell>
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<OverviewPage />} />
                  <Route path="apcas" element={<ApcasPage />} />
                  <Route path="equilibrium" element={<EquilibriumPage />} />
                  <Route path="adosa" element={<AdosaPage />} />
                  <Route path="mapas" element={<MapasPage />} />
                  <Route path="kpis" element={<KpisPage />} />
                  <Route path="economics" element={<EconomicsTab />} />
                </Route>

                {/* --- AIRCAP (Terminal Building Capacity) --- */}
                <Route path="/aircap" element={
                  <ProtectedRoute>
                    <AppShell><AirCapLogicPage /></AppShell>
                  </ProtectedRoute>
                } />

                {/* --- RUNCAP (Runway Capacity) --- */}
                <Route path="/runcap" element={
                  <ProtectedRoute>
                    <AppShell><RunCapPage /></AppShell>
                  </ProtectedRoute>
                } />

                {/* --- FTS (Fast Time Simulation) --- */}
                <Route path="/fts" element={
                  <ProtectedRoute>
                    <AppShell><SimulationHomePage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/fts/new" element={
                  <ProtectedRoute>
                    <AppShell><SimulationCreatePage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/fts/:id" element={
                  <ProtectedRoute>
                    <AppShell><SimulationDetailPage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/fts/:id/animation" element={
                  <ProtectedRoute>
                    <AppShell><SimulationAnimationPage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/training" element={
                  <ProtectedRoute>
                    <AppShell><AITrainingPage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/training/:sessionId" element={
                  <ProtectedRoute>
                    <AppShell><AITrainingPage /></AppShell>
                  </ProtectedRoute>
                } />

                {/* --- OPTIMIZER (SVS) --- */}
                <Route path="/optimizer" element={
                  <ProtectedRoute>
                    <AppShell><OptimizerPage /></AppShell>
                  </ProtectedRoute>
                } />

                {/* --- AIRPORT DASHBOARD (as-is vs to-be) --- */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <AppShell><AirportDashboardPage /></AppShell>
                  </ProtectedRoute>
                } />

                {/* --- COPILOT (RAG Data Assistant) --- */}
                <Route path="/copilot" element={
                  <ProtectedRoute>
                    <AppShell><CoPilotPage /></AppShell>
                  </ProtectedRoute>
                } />

                {/* --- UTILITY ROUTES --- */}
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <AppShell><UserProfilePage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <AppShell><SettingsPage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/projects/:id/reports" element={
                  <ProtectedRoute>
                    <AppShell><ReportPage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute>
                    <AppShell><ReportPage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/staff-roster" element={
                  <ProtectedRoute>
                    <AppShell><StaffRosterPage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/economics" element={
                  <ProtectedRoute>
                    <AppShell><EconomicsPage /></AppShell>
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AppShell><AdminPage /></AppShell>
                  </ProtectedRoute>
                } />

                {/* --- LEGACY REDIRECTS --- */}
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/operations/*" element={<Navigate to="/" replace />} />
                <Route path="/planning/*" element={<Navigate to="/" replace />} />
                <Route path="/intelligence/*" element={<Navigate to="/" replace />} />
                <Route path="/integration/*" element={<Navigate to="/" replace />} />
                <Route path="/simulations" element={<Navigate to="/fts" replace />} />
                <Route path="/simulations/*" element={<Navigate to="/fts" replace />} />
                <Route path="/digital-twin" element={<Navigate to="/" replace />} />
                <Route path="/acdm" element={<Navigate to="/" replace />} />
                <Route path="/vtt" element={<Navigate to="/" replace />} />
                <Route path="/sasa" element={<Navigate to="/" replace />} />
                <Route path="/pretactical" element={<Navigate to="/" replace />} />
                <Route path="/aop" element={<Navigate to="/" replace />} />
                <Route path="/aeroforecast" element={<Navigate to="/" replace />} />
                <Route path="/swim" element={<Navigate to="/" replace />} />
                <Route path="/aircap-logic" element={<Navigate to="/aircap" replace />} />

                {/* --- CATCH ALL --- */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
            </ShellStatusProvider>
          </CoPilotProvider>
        </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;